/**
 * EN — secondary. Any key missing here falls back to RU (docs/06 §8), which is why this
 * bundle is allowed to be a subset without breaking a screen.
 */
export const en = {
  common: {
    appName: 'MOST — admin',
    loading: 'Loading…',
    save: 'Save',
    cancel: 'Cancel',
    close: 'Close',
    confirm: 'Confirm',
    edit: 'Edit',
    view: 'View',
    delete: 'Delete',
    apply: 'Apply',
    reset: 'Reset',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    none: '—',
    yes: 'Yes',
    no: 'No',
    copy: 'Copy',
    copied: 'Copied',
    retry: 'Retry',
    back: 'Back',
    details: 'Details',
    expand: 'Expand',
    collapse: 'Collapse',
    page: 'Page',
    of: 'of',
    prev: 'Previous',
    next: 'Next',
    total: 'Total',
    noRating: 'no rating yet',
    desktopOnly: 'The admin panel is built for desktop',
    desktopOnlyHint:
      'These screens are dense — tables and a markup calculator. Best used on a computer, 1280 px or wider.',
    error: {
      title: 'Could not load data',
      description: 'Check the connection and try again.',
      retry: 'Retry',
    },
    empty: { title: 'Empty', nothingHere: 'Nothing here yet.' },
    liveOn: 'Live updates',
    liveOff: 'No connection — counters may be stale',
    liveOffShort: 'Offline',
    liveConnecting: 'Connecting…',
    saving: 'Saving…',
    saved: 'Saved',
    saveFailed: 'Could not save',
    unsavedChanges: 'You have unsaved changes. Leave the page?',
    unit: { piece: 'piece', pack: 'pack', box: 'box', pallet: 'pallet', truck: 'truck' },
    unitShort: { piece: 'pc', pack: 'pack', box: 'box', pallet: 'pallet', truck: 'truck' },
    perUnit: {
      piece: 'per piece',
      pack: 'per pack',
      box: 'per box',
      pallet: 'per pallet',
      truck: 'per truck',
    },
  },

  nav: {
    dashboard: 'Dashboard',
    approvals: 'Approvals',
    pricing: 'Pricing',
    orders: 'Orders',
    inbox: 'Support chat',
    users: 'Users',
    categories: 'Categories',
    notifications: 'Notifications',
    audit: 'Audit',
    settings: 'Settings',
    profile: 'Profile',
    language: 'Language',
    flavor: 'Theme colour',
    theme: 'Light / dark theme',
    logout: 'Log out',
    sectionQueues: 'Queues',
    sectionManage: 'Manage',
    sectionSystem: 'System',
  },

  palette: {
    open: 'Quick jump',
    placeholder: 'Where to? Start typing…',
    empty: 'Nothing found',
    sectionNav: 'Sections',
    sectionActions: 'Actions',
    hintNavigate: 'navigate',
    hintSelect: 'open',
    hintClose: 'close',
    actions: {
      urgentPricing: 'Urgent products — invisible to buyers',
      newOrders: 'New orders',
      deadNotifications: 'Undelivered notifications',
      pendingApprovals: 'Pending approvals',
    },
  },

  status: {
    user: {
      pending_email: 'Awaiting email confirmation',
      pending_approval: 'Awaiting approval',
      active: 'Active',
      blocked: 'Blocked',
    },
    order: {
      new: 'New',
      in_progress: 'In progress',
      completed: 'Completed',
      cancelled: 'Cancelled',
    },
    product: { draft: 'Draft', active: 'Active', archived: 'Archived' },
    notification: {
      pending: 'Queued',
      sent: 'Delivered',
      failed: 'Failed',
      dead: 'Not delivered',
    },
    channel: { telegram: 'Telegram', email: 'Email', inapp: 'In-app', ws: 'WebSocket' },
    role: { buyer: 'Buyer', producer: 'Producer', admin: 'Admin' },
    pricingFlag: {
      none: '—',
      base_price_changed: 'Base price changed',
      packaging_changed: 'Packaging changed',
    },
    market: { can_export: 'Can export', already_exports: 'Already exports' },
  },

  dashboard: {
    title: 'Dashboard',
    subtitle: 'What needs attention right now',
    counters: {
      pendingApprovals: 'Producers awaiting approval',
      unpricedProducts: 'Products without a selling price',
      urgentProducts: 'Urgent: hidden from the catalog',
      urgentHint: 'In the pricing queue and invisible to buyers right now',
      newOrders: 'New orders',
      failedNotifications: 'Failed notifications',
      deadNotifications: 'Undelivered notifications',
      deadHint:
        'A dead admin alert means an order arrived and nobody was told.',
    },
    recentAudit: 'Recent activity',
    recentAuditEmpty: 'No entries yet.',
    viewAllAudit: 'Full audit log',
    allClear: 'All queues are empty — nothing is waiting on you.',
    summary: {
      waiting_one: 'item waiting on you',
      waiting_other: 'items waiting on you',
      wrong_one: '{{count}} needs fixing now',
      wrong_other: '{{count}} need fixing now',
      live: 'Updates live',
    },
    groups: {
      wrong: 'Something is wrong',
      waiting: 'Work waiting',
    },
  },

  approvals: {
    title: 'Producer approvals',
    subtitle: 'Profiles with status "awaiting approval"',
    empty: 'Nobody is awaiting approval.',
    columns: {
      brand: 'Brand',
      email: 'Email',
      country: 'Country',
      products: 'Products',
      submitted: 'Submitted',
      status: 'Status',
      actions: 'Actions',
    },
    resubmitted: 'Re-submitted',
    previousRejection: 'Previous rejection reason',
    groupBuyerVisible: 'Visible to buyers',
    groupMostOnly: 'Visible to MOST only',
    fields: {
      brandName: 'Brand',
      country: 'Country',
      description: 'Description',
      productionCapacity: 'Production capacity',
      rating: 'Rating',
      legalName: 'Legal name',
      address: 'Address',
      contactPerson: 'Contact person',
      phone: 'Phone',
      email: 'Email',
      markets: 'Export markets',
      representativeContact: 'Representative contact',
      certificates: 'Certificates',
      noMarkets: 'No markets listed',
      noCertificates: 'No certificates uploaded',
    },
    approve: 'Approve',
    reject: 'Reject',
    approveConfirmTitle: 'Approve producer "{{brand}}"?',
    approveConfirmBody:
      'The account becomes active. The producer can add products, and they land in your pricing queue.',
    rejectConfirmTitle: 'Reject the profile of "{{brand}}"?',
    rejectConfirmBody:
      'The producer sees the reason verbatim, fixes the profile and re-submits. The status stays "awaiting approval" — there is no "rejected" status in the system.',
    rejectReasonLabel: 'Rejection reason',
    rejectReasonHint: 'Delivered to the producer verbatim. Say exactly what must be fixed.',
    rejectReasonRequired: 'A reason is required — it is sent to the producer.',
    approved: 'Producer "{{brand}}" approved',
    rejected: 'Profile "{{brand}}" rejected, reason sent',
  },

  pricing: {
    title: 'Pricing queue',
    subtitle: 'The catalog gate: without a selling price, buyers cannot see the product',
    empty: 'The queue is empty — every active product is priced.',
    emptyFiltered: 'Nothing in this category.',
    filters: {
      all: 'All',
      urgent: 'Urgent',
      flagged: 'Needs review',
    },
    urgentBanner_one: '{{count}} product is invisible to buyers',
    urgentBanner_other: '{{count}} products are invisible to buyers',
    urgentBannerHint:
      'Without a selling price a product never reaches the catalog. Set one to put it back on sale.',
    activatedOn: 'activated {{date}}',
    whatChanged: 'What changed',
    perPieceNote: '— prices per piece',
    columns: {
      product: 'Product',
      brand: 'Brand',
      basePrice: 'Base price (per piece)',
      sellingPrice: 'Selling price (per piece)',
      // The short forms, for the card list — it says "per piece" once, in the header.
      base: 'Base',
      selling: 'Selling',
      margin: 'Margin',
      flag: 'Reason',
      activated: 'Activated',
      actions: 'Actions',
    },
    urgentBadge: 'Urgent',
    urgentExplain: 'Product hidden from the catalog until priced',
    repriceChip: 'Needs review',
    unpriced: 'Not priced',
    oldBasePrice: 'Old base price',
    newBasePrice: 'New base price',
    marginAtCurrentPrice: 'Margin at the current selling price',
    marginNowNegative:
      'At the new base price the current selling price yields a negative margin. Re-price it.',
    setPrice: 'Set price',
    editPrice: 'Change price',
    flaggedAt: 'Flagged',

    calculator: {
      title: 'Markup calculator',
      forProduct: '{{product}} · {{brand}}',
      basePriceSection: "Producer's base price",
      basePricePerPiece: 'Per piece (normalized)',
      basePriceContext: 'For context',
      enteredAs: 'Producer entered: {{value}} {{unit}}',
      inputsSection: 'Markup',
      markupPct: 'Markup, %',
      sellingPrice: 'Selling price per piece, $',
      syncHint: 'The two fields are linked — edit either and the other recomputes.',
      previewSection: 'What the buyer will see',
      previewHint: 'Selling price at every packaging level.',
      marginSection: 'Margin',
      marginUsd: 'Margin per piece',
      marginPctLabel: 'Margin, %',
      negativeMarginTitle: 'Negative markup',
      negativeMarginBody:
        "The selling price is below the producer's base price — you are selling at a loss.",
      confirmNegative: 'Confirm the negative markup',
      publish: 'Publish',
      republish: 'Save price',
      withdraw: 'Withdraw from catalog',
      withdrawHint:
        'Clears the selling price. The product vanishes from the buyer catalog immediately and returns to this queue.',
      withdrawConfirmTitle: 'Withdraw "{{product}}" from the catalog?',
      withdrawConfirmBody:
        'The selling price is cleared. The product disappears from the buyer catalog and search at once and returns to the pricing queue as unpriced. Orders already submitted are unaffected — their prices are snapshots.',
      priceRequired: 'Enter a selling price.',
      priceMustBePositive: 'The selling price must be greater than zero.',
      priceInvalid: 'Enter a number.',
      confirmNegativeRequired:
        'Tick "confirm the negative markup" to save a price below the base price.',
      publishConfirmTitle: 'Publish "{{product}}" at a negative markup?',
      publishConfirmBody:
        'The selling price {{selling}} is below the base price {{base}}. Margin: {{margin}} ({{pct}}). The product becomes buyable immediately.',
      published: 'Product "{{product}}" published',
      publishedHint: 'Buyers can see it now.',
      undo: 'Undo',
      undone: 'Publish undone — the product is hidden again',
      withdrawn: 'Product "{{product}}" withdrawn',
      storedNote:
        'Only the absolute price per piece is stored. The percentage is never persisted — a stored ratio would silently re-price the product every time the base price moved.',
    },
  },

  orders: {
    title: 'Orders',
    subtitle: 'Every order, with margin',
    empty: 'No orders yet.',
    columns: {
      number: 'Number',
      date: 'Date',
      buyer: 'Buyer',
      status: 'Status',
      items: 'Items',
      total: 'Total',
      margin: 'Margin',
      truckFill: 'Truck fill',
      actions: 'Actions',
    },
    filterStatus: 'Order status',
    open: 'Open',
    changeStatus: 'Change status',
    statusConfirmTitle: 'Move order {{number}} to "{{status}}"?',
    statusConfirmBody: 'The buyer is notified. The change is written to the audit log.',
    statusConfirmTerminal:
      'This is a terminal status — the order cannot be reopened afterwards. The buyer is notified.',
    statusChanged: 'Order {{number}}: status changed',
    terminalHint: 'The order is in a terminal status — no further changes.',

    /* The order drawer — a preview of the contents, not a replacement for the full page. */
    drawer: {
      title: 'Order {{number}}',
      buyer: 'Buyer',
      items: 'Order items',
      pieces: 'pcs',
      weight: 'Weight',
      truckFill: 'Truck fill',
      total: 'Total',
      margin: 'Margin',
      openFull: 'Open in full',
    },

    detail: {
      title: 'Order {{number}}',
      buyerSection: 'Buyer',
      buyerHint: 'Full contact details — you have to phone this person.',
      company: 'Company',
      contactPerson: 'Contact person',
      phone: 'Phone',
      email: 'Email',
      country: 'Country',
      city: 'City',
      rating: 'Buyer rating',
      ratingPrivate: 'Visible to you only — never to the buyer, never to producers.',
      commentSection: "Buyer's comment",
      commentHint: 'For you only. Never forwarded to a producer.',
      noComment: 'No comment left.',
      compositionSection: 'Order composition',
      compositionHint: 'Grouped by producer. Each producer sees only their own slice.',
      producerGroup: 'Producer: {{brand}}',
      producerSubtotal: 'Producer subtotal',
      lineColumns: {
        product: 'Product',
        qty: 'Quantity',
        pieces: 'Pieces',
        basePrice: 'Base (per piece)',
        sellingPrice: 'Selling (per piece)',
        lineTotal: 'Line total',
        lineMargin: 'Margin',
        weight: 'Weight',
      },
      totalsSection: 'Totals',
      totalUsd: 'Total at selling prices',
      totalMargin: 'Margin',
      totalWeight: 'Weight',
      truckFill: 'Truck fill',
      truckFillOver: 'The order exceeds one truck',
      notificationsSection: 'Notification delivery',
      notificationsHint: 'Who was told about this order, and over which channel.',
      noNotifications: 'No notifications for this order.',
    },
  },

  users: {
    title: 'Users',
    subtitle: 'Buyers and producers',
    empty: 'No users found.',
    searchPlaceholder: 'Email, company, brand…',
    filterRole: 'Role',
    filterStatus: 'Status',
    columns: {
      name: 'Name',
      email: 'Email',
      role: 'Role',
      status: 'Status',
      registered: 'Registered',
      lastLogin: 'Last login',
      actions: 'Actions',
    },
    neverLoggedIn: 'never',
    detail: {
      title: 'User',
      profileSection: 'Profile',
      telegramLinked: 'Telegram linked',
      telegramNotLinked: 'Telegram not linked',
      locale: 'Language',
    },
    rating: {
      title: 'Rating',
      producerPublic: 'Public rating',
      producerPublicHint: 'Shown to buyers — on product cards and the producer page.',
      buyerPrivate: 'Private note',
      buyerPrivateHint:
        'Visible to you only. Never shown to the buyer, never to producers.',
      label: 'Rating (1.0–5.0)',
      clear: 'Clear rating',
      invalid: 'A rating is a number from 1.0 to 5.0 with one decimal.',
      saved: 'Rating saved',
      cleared: 'Rating cleared',
    },
    block: 'Block',
    unblock: 'Unblock',
    blockConfirmTitle: 'Block "{{name}}"?',
    blockConfirmBody:
      'Every session this user holds is revoked immediately — they are signed out on every device. Reversible.',
    unblockConfirmTitle: 'Unblock "{{name}}"?',
    unblockConfirmBody: 'The user can sign in again.',
    blocked: 'User blocked',
    unblocked: 'User unblocked',
    verifyEmail: 'Mark email verified',
    verifyEmailHint: 'A last resort when mail does not arrive. The action is audited.',
    verifyEmailConfirmTitle: 'Mark "{{email}}" as verified?',
    verifyEmailConfirmBody:
      'Normally the user verifies their own email from the link in the message. Use this only when mail does not arrive. The action is written to the audit log.',
    verifyEmailDone: 'Email marked verified',

    create: {
      button: 'Create user',
      title: 'Create a user manually',
      hint: 'The fallback when a producer cannot self-register or email does not arrive. The account is created already email-verified.',
      role: 'Role',
      email: 'Email',
      locale: 'Interface and notification language',
      companyName: 'Company',
      contactPerson: 'Contact person',
      phone: 'Phone',
      country: 'Country (2-letter code)',
      city: 'City',
      brandName: 'Brand',
      submit: 'Create',
      created: 'User created',
      passwordTitle: 'Temporary password',
      passwordBody:
        'The server generated this password and shows it ONCE. Copy it and pass it to the user — over Telegram, for instance. It cannot be retrieved again.',
      passwordForceChange: 'The user must change it at first login.',
      passwordAck: 'I have copied the password',
      passwordCopyFirst: 'Copy the password first — afterwards it is gone.',
      errors: {
        emailRequired: 'Enter an email.',
        emailInvalid: 'Invalid email.',
        roleRequired: 'Choose a role.',
        brandRequired: 'A producer needs a brand name.',
        companyRequired: 'A buyer needs a company name.',
        contactRequired: 'A buyer needs a contact person.',
        phoneRequired: 'A buyer needs a phone number.',
        countryRequired: 'Enter the country — a 2-letter code.',
        countryFormat: 'A 2-letter country code, e.g. RU or UZ.',
        cityRequired: 'A buyer needs a city.',
      },
    },
  },

  categories: {
    search: 'Search by name or slug…',
    noResults: 'Nothing matches "{{query}}".',
    stats: {
      total: 'Categories',
      active: 'Active',
      products: 'Products in catalog',
      subcategories: 'Subcategories',
      rootsHint_one: '{{count}} root',
      rootsHint_other: '{{count}} roots',
      nestedHint_one: 'under {{count}} category',
      nestedHint_other: 'under {{count}} categories',
      activeHint: 'of {{total}} visible to buyers',
      productsHint_one: 'across {{count}} category',
      productsHint_other: 'across {{count}} categories',
    },
    subcategories_one: '{{count}} subcategory',
    subcategories_other: '{{count}} subcategories',
    noSubcategories: 'No subcategories',
    inactive: 'Inactive',
    expand: 'Show subcategories',
    collapse: 'Hide subcategories',
    addSub: 'Add subcategory',
    title: 'Categories',
    subtitle: 'Names in three languages, sort order, active flag',
    empty: 'No categories yet.',
    columns: {
      slug: 'Slug',
      nameRu: 'Name (RU)',
      nameEn: 'Name (EN)',
      nameTk: 'Name (TK)',
      sortOrder: 'Order',
      products: 'Products',
      active: 'Active',
      actions: 'Actions',
    },
    draftNote: 'EN and TK translations are DRAFT pending the owner. Edit them here.',
    localeName: 'Name language',
    parentLabel: 'Parent category',
    parentNone: '— root category —',
    parentHasChildrenHint:
      'This category has subcategories, so it cannot become one itself. Only one level of nesting is allowed.',
    add: 'Add category',
    edit: 'Edit category',
    formTitleNew: 'New category',
    formTitleEdit: 'Category "{{name}}"',
    deactivateWarningTitle: 'Deactivate category "{{name}}"?',
    deactivateWarningBody_one:
      '{{count}} product will disappear from the buyer catalog and search. It stays visible in the producer cabinet and here. Reversible.',
    deactivateWarningBody_other:
      '{{count}} products will disappear from the buyer catalog and search. They stay visible in the producer cabinet and here. Reversible.',
    deactivateWarningEmpty:
      'This category has no products — deactivating it changes nothing. Reversible.',
    deactivateWarningChildren: 'Its subcategories are hidden along with it, products and all.',
    noDelete: 'Categories are never deleted — only deactivated.',
    saved: 'Category saved',
    saveFailed: 'Could not save the category',
    errors: {
      nameRuRequired: 'The Russian name is required.',
      nameEnRequired: 'The English name is required.',
      nameTkRequired: 'The Turkmen name is required.',
      sortOrderRequired: 'Enter a sort order.',
      sortOrderInteger: 'The sort order is a whole number.',
    },
  },

  notifications: {
    title: 'Notification health',
    subtitle: 'The outbox: what was sent, what failed, what never arrived',
    empty: 'No notifications.',
    deadWarning_one:
      '{{count}} notification was never delivered. Check it — an order may have gone unheard.',
    deadWarning_other:
      '{{count}} notifications were never delivered. Check them — orders may have gone unheard.',
    columns: {
      created: 'Created',
      recipient: 'Recipient',
      type: 'Type',
      channel: 'Channel',
      status: 'Status',
      attempts: 'Attempts',
      nextRetry: 'Next retry',
      lastError: 'Last error',
      sentAt: 'Sent',
      actions: 'Actions',
    },
    forAdmin: 'You',
    showDead: 'Show dead',
    refreshing: 'Refreshing…',
    emptyFiltered: 'No notifications match this filter.',
    clearFilters: 'Clear filters',
    stats: {
      pending: 'Queued',
      pendingHint: 'Awaiting delivery',
      failed: 'Failed',
      failedHint: 'Still retrying automatically',
      dead: 'Dead',
      deadHint: 'Retries exhausted — needs a manual retry',
    },
    types: {
      order_created_admin: 'New order — your alert',
      order_created_producer: 'New order — producer copy',
      order_created_buyer: 'New order — buyer confirmation',
      order_status_changed: 'Order status changed',
      producer_approved: 'Producer approved',
      producer_rejected: 'Producer rejected',
      product_priced: 'Product published — price set',
      email_verification: 'Email verification',
      password_reset: 'Password reset',
      product_unpublished_margin_guard: 'Product unpublished — margin guard',
      chat_message_admin: 'Support chat message',
    },
    filterStatus: 'Status',
    filterChannel: 'Channel',
    filterType: 'Type',
    filterTypePlaceholder: 'e.g. order_created',
    retry: 'Retry',
    retryHint: 'Re-queues it: status back to "queued", attempts back to 0.',
    retryConfirmTitle: 'Retry delivery?',
    retryConfirmBody:
      'The notification returns to the queue with status "queued" and its attempt counter reset to 0. The action is audited.',
    retried: 'Notification re-queued',
    inspect: 'Inspect payload',
    payloadTitle: 'Notification payload',
    payloadHint:
      'This is exactly what the recipient got: the payload was audience-filtered when the row was written, not when it is read.',
    noError: 'No errors.',
  },

  audit: {
    title: 'Audit log',
    subtitle: 'History of actions. Read-only — entries are never edited or deleted.',
    empty: 'No entries.',
    columns: {
      when: 'When',
      actor: 'Actor',
      action: 'Action',
      entity: 'Entity',
      entityId: 'Entity ID',
    },
    system: 'System',
    filterActor: 'Actor ID',
    filterAction: 'Action',
    filterActionPlaceholder: 'e.g. product.price_set',
    filterEntity: 'Entity',
    filterEntityPlaceholder: 'e.g. product',
    filterFrom: 'From',
    filterTo: 'To',
    meta: 'Details',
    noMeta: 'No details.',
    actions: {
      'producer.approved': 'Producer approved',
      'producer.rejected': 'Producer rejected',
      'producer.rated': 'Producer rated',
      'buyer.rated': 'Buyer rated',
      'user.created': 'User created',
      'user.blocked': 'User blocked',
      'user.unblocked': 'User unblocked',
      'user.email_verified_manually': 'Email verified manually',
      'product.price_set': 'Product price set',
      'category.created': 'Category created',
      'category.updated': 'Category updated',
      'order.status_changed': 'Order status changed',
      'notification.retried': 'Notification retried',
      'role.created': 'Role created',
      'role.updated': 'Role updated',
      'role.deleted': 'Role deleted',
      'role.assigned': 'Role assigned',
    },
    entities: {
      producer: 'Producer',
      product: 'Product',
      user: 'User',
      order: 'Order',
      buyer: 'Buyer',
      category: 'Category',
      role: 'Role',
      notification: 'Notification',
    },
  },

  settings: {
    title: 'Settings',
    subtitle: 'Monitoring, roles & permissions, and the audit log.',
    tabs: {
      monitoring: 'Monitoring',
      roles: 'Roles & permissions',
      audit: 'Audit log',
    },
    categories: {
      approvals: 'Approvals',
      pricing: 'Pricing',
      orders: 'Orders',
      users: 'Users',
      catalog: 'Catalog',
      system: 'System',
    },
    monitoring: {
      title: 'Monitoring',
      subtitle: 'System health at a glance. Read-only — refreshes automatically.',
      infra: 'Infrastructure',
      infraHint: 'Live connectivity to the datastores and the live channel.',
      database: 'Database',
      redis: 'Redis',
      liveChannel: 'Live channel',
      outbox: 'Notification delivery',
      outboxHint: 'The outbox worker. A climbing “pending” means delivery is falling behind.',
      pending: 'Pending',
      pendingHint: 'Queued for delivery',
      failed: 'Failed',
      dead: 'Dead',
      deadHint: 'Given up — needs a manual retry',
      queues: 'Work queues',
      pendingApprovals: 'Pending approvals',
      unpriced: 'Unpriced',
      urgent: 'Urgent (unpriced & live)',
      newOrders: 'New orders',
      constants: 'Truck constants',
      truckPallets: 'Pallets per truck',
      truckMaxKg: 'Max kg per truck',
    },
    roles: {
      title: 'Roles & permissions',
      subtitle:
        'Custom roles bundle admin capabilities. They never affect the buyer/producer anonymity boundary.',
      create: 'New role',
      createTitle: 'New role',
      editTitle: 'Edit role',
      viewTitle: '{{name}} (built in)',
      dialogHint: 'A role grants a set of admin capabilities. Buyers and producers are unaffected.',
      empty: 'No roles yet.',
      name: 'Name',
      description: 'Description',
      capabilities: 'Capabilities',
      builtin: 'Built in',
      permCount: '{{count}} permissions',
      permCount_one: '{{count}} permission',
      memberCount: '{{count}} members',
      memberCount_one: '{{count}} member',
      created: 'Role created.',
      saved: 'Role saved.',
      deleted: 'Role deleted.',
      deleteTitle: 'Delete role «{{name}}»?',
      deleteHint:
        'Admins holding this role revert to full access. This does not delete any account.',
      errors: {
        nameRequired: 'A name is required.',
        nameTaken: 'A role with this name already exists.',
      },
    },
  },

  profile: {
    title: 'Profile',
    subtitle: 'Your account: language, password and Telegram',
    account: {
      title: 'Account',
    },
    language: {
      title: 'Language',
      hint: 'Interface and notification language — Telegram messages and emails arrive in it.',
      saved: 'Language saved',
    },
    password: {
      title: 'Change password',
      hint: 'Changing the password ends every other session. This one stays.',
      current: 'Current password',
      new: 'New password',
      newHint: 'At least 10 characters.',
      confirm: 'Repeat new password',
      submit: 'Change password',
      changed: 'Password changed. Other sessions were ended.',
      errors: {
        required: 'This field is required.',
        too_short: 'At least 10 characters.',
        mismatch: 'Passwords do not match.',
        incorrect: 'The current password is wrong.',
      },
    },
    telegram: {
      title: 'Telegram',
      hint: 'The primary channel for order and queue alerts — more reliable than email in the region.',
      linked: 'Telegram linked',
      linkedAt: 'linked {{date}}',
      notLinked: 'Telegram is not linked — alerts arrive by email and in-app only.',
      connect: 'Link Telegram',
      connectAgain: 'Open the link again',
      awaiting: 'Waiting for the link — confirm in Telegram and come back to this tab.',
      unlink: 'Unlink',
      unlinked: 'Telegram unlinked. Alerts will arrive by email and in-app.',
      linkError: 'Could not create the link. Try again.',
    },
  },

  errors: {
    forbidden: 'Access denied',
    forbiddenBody: 'This area is for the administrator only.',
    goToOwnApp: 'Go to your app',
    accountUnavailable: 'Account unavailable',
    accountUnavailableBody: 'Contact MOST.',
    mustChangePassword: 'Password change required',
    mustChangePasswordBody: 'Sign in with your temporary password and change it in your own app.',
    notFound: 'Page not found',
    notFoundBody: 'No such page.',
    goHome: 'Go to dashboard',
    conflict: 'The state changed',
    conflictBody: 'The data changed underneath you. Refresh and try again.',
    generic: 'Something went wrong',
    genericBody: 'Try again. If it keeps happening, tell the developer.',
  },
  inbox: {
    title: 'Support chat',
    emptyList: 'Nobody has written yet',
    noThread: 'Select a conversation',
    emptyThread: 'No messages yet',
    placeholder: 'Reply…',
    send: 'Send',
    attach: 'Attach a file',
    record: 'Record a voice message',
    stopRecording: 'Stop recording',
    loadOlder: 'Show earlier messages',
    today: 'Today',
    yesterday: 'Yesterday',
    scrollDown: 'Jump to latest messages',
    sendError: 'Could not send. Please try again.',
    callAudio: 'Audio call',
    callVideo: 'Video call',
    role: { buyer: 'Buyer', producer: 'Producer' },
    kind: { text: 'Message', image: 'Image', video: 'Video', audio: 'Audio', file: 'File', call: 'Call' },
    call: {
      outgoing: 'Calling…',
      incoming: 'Incoming call',
      connecting: 'Connecting…',
      active: 'In call',
      endedStatus: 'Call ended',
      accept: 'Answer',
      decline: 'Decline',
      hangUp: 'Hang up',
      completed: 'Call',
      missed: 'Missed call',
      declined: 'Call declined',
      mediaDenied: 'No access to microphone or camera',
      connectionLost: 'Connection lost',
      failed: 'Could not start the call',
    },
  },
} as const;
